/**
 * The mail-connections half of the KnowIt adapter: `mail.*`.
 *
 * Same bearer-injected transport as `knowitGit.ts`. Every verb is the caller's OWN mailbox — the
 * server scopes by workspace AND person, so there is no "list everyone's" here on purpose.
 */
import type {
  MailActivity,
  MailConnection,
  MailConnectionPatch,
  MailConnectionStatus,
  MailImportAs,
  MailImportResult,
  MailInboxMessage,
  MailProviderName,
  MailProviders,
  MailSendInput,
  MailSyncReport,
} from "~/api/mailTypes";

export interface MailTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

export interface MailApi {
  providers(): Promise<MailProviders>;
  connections: {
    list(): Promise<MailConnection[]>;
    /** The provider's consent page; it brings the person back to `returnTo` with `?mailConnected=` or `?mailError=`. */
    connectStart(provider: MailProviderName, returnTo: string): Promise<string>;
    /** Stores the grant the provider returned (`?mailPending=<code>`) — only for the person who started. */
    connectFinish(code: string): Promise<MailConnection>;
    update(input: MailConnectionPatch): Promise<MailConnection>;
    sync(id: string): Promise<MailSyncReport>;
    remove(id: string): Promise<void>;
    inbox(id: string, max?: number): Promise<MailInboxMessage[]>;
    import(
      id: string,
      messageId: string,
      as: MailImportAs,
      target?: { collectionId?: string; projectId?: string },
    ): Promise<MailImportResult>;
    activity(id: string, max?: number): Promise<MailActivity[]>;
  };
  send(input: MailSendInput): Promise<void>;
}

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" ? (value as Json) : {};
}
function arr(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(obj) : [];
}
function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

const STATUSES: MailConnectionStatus[] = ["ok", "failing", "reauth", "disabled"];

export function toMailConnection(raw: Json): MailConnection {
  const status = str(raw.status);
  const knowledge = obj(raw.knowledge);
  const tiles = obj(raw.tiles);
  return {
    id: str(raw.id),
    provider: raw.provider === "microsoft" ? "microsoft" : "google",
    address: str(raw.address),
    displayName: strOrNull(raw.displayName),
    status: (STATUSES as string[]).includes(status) ? (status as MailConnectionStatus) : "ok",
    lastError: strOrNull(raw.lastError),
    lastSyncAt: strOrNull(raw.lastSyncAt),
    connectedAt: str(raw.connectedAt, new Date(0).toISOString()),
    knowledge: {
      enabled: knowledge.enabled === true,
      collectionId: strOrNull(knowledge.collectionId),
      label: str(knowledge.label, "Docit"),
    },
    tiles: {
      enabled: tiles.enabled === true,
      projectId: strOrNull(tiles.projectId),
      label: str(tiles.label, "Docit-Task"),
    },
    repliesEnabled: raw.repliesEnabled !== false,
    sendEnabled: raw.sendEnabled !== false,
    canSend: raw.canSend === true,
  };
}

function toActivity(raw: Json): MailActivity {
  const purpose = str(raw.purpose);
  const outcome = str(raw.outcome);
  return {
    id: str(raw.id),
    purpose: purpose === "tile" || purpose === "reply" ? purpose : "knowledge",
    outcome: outcome === "failed" || outcome === "unmatched" ? outcome : "done",
    subject: strOrNull(raw.subject),
    from: strOrNull(raw.from),
    receivedAt: strOrNull(raw.receivedAt),
    at: str(raw.at, new Date(0).toISOString()),
    note: strOrNull(raw.note),
    documentUrlId: strOrNull(raw.documentUrlId),
    workItemKey: strOrNull(raw.workItemKey),
  };
}

export function createKnowItMail(transport: MailTransport): MailApi {
  const post = async (method: string, body: Record<string, unknown> = {}) =>
    obj(await transport.post(method, body));
  return {
    providers: async () => {
      const data = obj((await post("mail.providers")).data);
      return {
        providers: arr(data.providers).map((p) => ({
          name: p.name === "microsoft" ? ("microsoft" as const) : ("google" as const),
          label: str(p.label),
          configured: p.configured === true,
        })),
        secretsAvailable: data.secretsAvailable === true,
        knowledgeLabel: str(data.knowledgeLabel, "Docit"),
        tilesLabel: str(data.tilesLabel, "Docit-Task"),
      };
    },
    connections: {
      list: async () => arr((await post("mail.connections.list")).data).map(toMailConnection),
      connectStart: async (provider, returnTo) =>
        str(obj((await post("mail.connections.connectStart", { provider, returnTo })).data).url),
      connectFinish: async (code) =>
        toMailConnection(obj((await post("mail.connections.connectFinish", { code })).data)),
      update: async (input) => {
        const body: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(input)) {
          if (v !== undefined) body[k] = v;
        }
        return toMailConnection(obj((await post("mail.connections.update", body)).data));
      },
      sync: async (id) => {
        const data = obj((await post("mail.connections.sync", { id })).data);
        return {
          connection: toMailConnection(obj(data.connection)),
          knowledge: num(data.knowledge),
          tiles: num(data.tiles),
          answers: num(data.answers),
          unmatched: num(data.unmatched),
          failed: num(data.failed),
        };
      },
      remove: async (id) => {
        await post("mail.connections.remove", { id });
      },
      inbox: async (id, max) =>
        arr((await post("mail.connections.inbox", { id, ...(max ? { max } : {}) })).data).map(
          (m) => ({
            id: str(m.id),
            subject: strOrNull(m.subject),
            from: strOrNull(m.from),
            at: strOrNull(m.at),
            snippet: strOrNull(m.snippet),
            link: strOrNull(m.link),
            documentId: strOrNull(m.documentId),
            workItemId: strOrNull(m.workItemId),
          }),
        ),
      import: async (id, messageId, as, target = {}) => {
        const data = obj(
          (
            await post("mail.connections.import", {
              id,
              messageId,
              as,
              ...(target.collectionId ? { collectionId: target.collectionId } : {}),
              ...(target.projectId ? { projectId: target.projectId } : {}),
            })
          ).data,
        );
        return {
          documentId: strOrNull(data.documentId),
          documentUrlId: strOrNull(data.documentUrlId),
          workItemId: strOrNull(data.workItemId),
          workItemKey: strOrNull(data.workItemKey),
        };
      },
      activity: async (id, max) =>
        arr((await post("mail.connections.activity", { id, ...(max ? { max } : {}) })).data).map(
          toActivity,
        ),
    },
    send: async (input) => {
      await post("mail.send", {
        id: input.id,
        to: input.to,
        cc: input.cc ?? [],
        subject: input.subject,
        body: input.body,
      });
    },
  };
}

/**
 * The offline client's mail surface: no provider is configured, so nothing can be connected —
 * and faking an inbox would show mail that does not exist.
 */
export function createFakeMail(): MailApi {
  const refuse = async (): Promise<never> => {
    throw new Error("Mail connections need the real API.");
  };
  return {
    providers: async () => ({
      providers: [
        { name: "google", label: "Gmail", configured: false },
        { name: "microsoft", label: "Outlook / Microsoft 365", configured: false },
      ],
      secretsAvailable: false,
      knowledgeLabel: "Docit",
      tilesLabel: "Docit-Task",
    }),
    connections: {
      list: async () => [],
      connectStart: refuse,
      connectFinish: refuse,
      update: refuse,
      sync: refuse,
      remove: refuse,
      inbox: refuse,
      import: refuse,
      activity: async () => [],
    },
    send: refuse,
  };
}
